/**
 * TermsAcceptanceModal
 * 
 * Modal shown after login when the user needs to accept updated terms.
 * Only appears when TERMS_ACCEPTANCE_MODE is 'hard' and user hasn't accepted.
 * 
 * This is a blocking modal - user cannot proceed without accepting.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePublicConfig } from '@/core/hooks/usePublicConfig';
import { useUserAcceptTerms } from '@/core/api/generated/user/user';
import { ConsentModalBase } from '@/features/auth/components/ConsentModalBase';

interface TermsAcceptanceModalProps {
  isOpen: boolean;
  onAccept: () => void;
}

export function TermsAcceptanceModal({
  isOpen,
  onAccept,
}: TermsAcceptanceModalProps) {
  const { t } = useTranslation();
  const { config: publicConfig } = usePublicConfig();
  const { mutateAsync: acceptTerms, isPending } = useUserAcceptTerms();
  const [error, setError] = useState<string | null>(null);

  // Get terms content from public config
  const termsContent = publicConfig?.termsAndConditionsContent || 
    `# Terms and Conditions\n\nPlease configure terms and conditions in System Settings > Legal > Terms & Conditions.`;

  // Determine mode from config (default to 'hard' since this modal only shows in hard mode)
  const mode = 'hard' as const;

  const handleAccept = async (signedFullName?: string) => {
    try {
      setError(null);
      await acceptTerms({
        data: {
          signedFullName: signedFullName || null,
        },
      });
      onAccept();
    } catch (err) {
      console.error('[TermsAcceptanceModal] Failed to accept terms:', err);
      setError(t('common.error') || 'Failed to accept terms. Please try again.');
    }
  };

  // This modal cannot be closed without accepting
  const handleClose = () => {
    // Do nothing - modal is blocking
  };

  return (
    <ConsentModalBase
      isOpen={isOpen}
      onClose={handleClose}
      onAccept={handleAccept}
      mode={mode}
      title={t('auth.terms.title') || 'Terms and Conditions'}
      content={termsContent}
      isLoading={isPending}
      error={error}
      // No close button - must accept
      showCloseButton={false}
    />
  );
}

